import { getHistory } from '@/config/history'

/**
 * A single URL query-param value, as Scrivito models it.
 */
export type UrlParamValue = string | null | Array<string | null>

/**
 * A patch applied to the current URL params. Keys whose value is `null` or
 * `undefined` are removed from the URL; every other key is written verbatim.
 */
export type UrlParamsPatch = {
  [key: string]: UrlParamValue | undefined
}

/**
 * `true` when the value carries no information and should be treated as absent:
 * an empty string, the literal string `"undefined"` or `"NaN"` (stringified
 * leaks from buggy writers), `null`, or an empty array.
 */
function isEmptyUrlValue(value: UrlParamValue): boolean {
  return (
    value === null ||
    value === '' ||
    value === 'undefined' ||
    value === 'NaN' ||
    (Array.isArray(value) && value.length === 0)
  )
}

/**
 * Merge a patch into the live URL query params.
 *
 * Reads the **live** `history.location.search` and writes synchronously, so
 * multiple writers in the same render cycle compose instead of clobbering each
 * other. Empty values (`null`, `undefined`, `''`, `'undefined'`, `'NaN'`, `[]`)
 * delete the key.
 *
 * @param patch  — keys to set/delete
 * @param replace — `true` to replace the current history entry (no Back entry).
 *   Use for mount-time defaults. Default `false` pushes a new entry so the user
 *   can undo with Back.
 */
export function writeUrlParams(patch: UrlParamsPatch, replace = false): void {
  const history = getHistory()
  if (!history) {
    return
  }

  const { pathname, search, hash } = history.location
  const next = new URLSearchParams(search)

  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined || value === null || isEmptyUrlValue(value)) {
      next.delete(key)
    } else if (Array.isArray(value)) {
      next.delete(key)
      for (const item of value) {
        if (item !== null) {
          next.append(key, item)
        }
      }
    } else {
      next.set(key, value)
    }
  }

  const nextQuery = next.toString()
  const nextSearch = nextQuery ? `?${nextQuery}` : ''
  if (nextSearch === search) {
    return
  }

  if (replace) {
    history.replace({ pathname, search: nextSearch, hash })
  } else {
    history.push({ pathname, search: nextSearch, hash })
  }
}

/**
 * Finds which option is currently selected by matching `current` against
 * `option.value`. Returns `fallback` when no match is found or `current` is
 * empty. Works for any dropdown — single-value (status, type) or range-preset
 * (when pointed at the stored preset key rather than the raw timestamp).
 */
export function matchUrlOption<T extends string | boolean>(
  options: Array<{ value: T }>,
  current: string | null | undefined,
  fallback: T,
): T {
  if (!current) return fallback
  return options.find((o) => String(o.value) === current)?.value ?? fallback
}
